<template>
  <div>
    <v-card class="mb-4">
      <v-card-title>
        <v-icon color="primary" class="me-2">mdi-calendar-remove</v-icon>
        {{ $t('closures') }}
        <v-spacer></v-spacer>
        <v-btn color="primary" @click="openAdd">
          <v-icon left>mdi-plus</v-icon>
          {{ $t('add_a_closure') }}
        </v-btn>
      </v-card-title>
      <v-card-subtitle>{{ $t('closures_hint') }}</v-card-subtitle>

      <v-card-text>
        <vue-element-loading :active="isLoading" />

        <empty-state
          v-if="!isLoading && !closures.length"
          icon="mdi-calendar-remove"
          :title="$t('no_closures_yet')"
          :body="$t('no_closures_yet_hint')"
          :action="$t('add_a_closure')"
          @action="openAdd"
        ></empty-state>

        <v-simple-table v-else-if="closures.length" dense>
          <template #default>
            <thead>
              <tr>
                <th>{{ $t('from') }}</th>
                <th>{{ $t('to') }}</th>
                <th class="text-end">{{ $t('days') }}</th>
                <th>{{ $t('reason') }}</th>
                <th>{{ $t('status') }}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="closure in closures" :key="closure.id">
                <td class="text-no-wrap">{{ closure.starts_on }}</td>
                <td class="text-no-wrap">{{ closure.ends_on }}</td>
                <td class="text-end">{{ closure.days }}</td>
                <td>{{ closure.reason }}</td>
                <td>
                  <v-chip x-small :color="stateColour(closure.state)" outlined>
                    {{ $t('closure_' + closure.state) }}
                  </v-chip>
                </td>
                <td class="text-end">
                  <v-btn
                    small
                    text
                    color="error"
                    :loading="removing === closure.id"
                    @click="remove(closure)"
                  >
                    {{ closure.state === 'past' ? $t('delete') : $t('cancel_closure') }}
                  </v-btn>
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
      </v-card-text>
    </v-card>

    <v-dialog v-model="addDialog" max-width="560" no-click-animation @keydown.esc="addDialog = false">
      <v-card>
        <v-card-title>
          <v-icon color="primary" class="me-2">mdi-calendar-remove</v-icon>
          <span class="text-h6">{{ $t('add_a_closure') }}</span>
        </v-card-title>
        <v-card-subtitle class="pt-2">{{ $t('add_a_closure_hint') }}</v-card-subtitle>

        <v-card-text>
          <!-- one range, picked as two ends: a single day is both ends the same -->
          <v-date-picker
            v-model="range"
            range
            full-width
            no-title
            :min="today"
          ></v-date-picker>

          <v-text-field
            v-model="reason"
            :label="$t('reason')"
            :hint="$t('closure_reason_hint')"
            persistent-hint
            dense
            outlined
            class="mt-4"
          ></v-text-field>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text :disabled="isSaving" @click="addDialog = false">{{ $t('cancel') }}</v-btn>
          <v-btn
            color="primary"
            text
            :loading="isSaving"
            :disabled="!range.length"
            @click="save"
          >
            {{ $t('close_the_school') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
/*
 * The days the school is shut.
 *
 * There was no way to say it. A school closing for half-term could delete its
 * trips and rebuild them afterwards, unassign every driver, or do nothing - and
 * doing nothing is what happens, because the other two are worse. Every morning
 * of that week then published its runs, booked every child onto them, and spent
 * a coin for each of them, for buses nobody boarded.
 *
 * Said in advance, none of that happens. Said late - the boiler failed this
 * morning - the runs are withdrawn, the bookings cancelled and the coins given
 * back, and the screen says how many of each so the school can see it happened.
 */
import VueElementLoading from "vue-element-loading";
import EmptyState from "@/components/EmptyState";

export default {
  name: "ClosuresIndex",
  components: { VueElementLoading, EmptyState },
  data() {
    return {
      closures: [],
      today: "",
      range: [],
      reason: "",
      addDialog: false,
      isLoading: false,
      isSaving: false,
      removing: null,
    };
  },
  mounted() {
    this.load();
  },
  methods: {
    stateColour(state) {
      return { past: "", current: "error", upcoming: "warning" }[state] || "";
    },
    openAdd() {
      this.range = [];
      this.reason = "";
      this.addDialog = true;
    },
    load() {
      this.isLoading = true;
      axios
        .get("/school-closures/all")
        .then((response) => {
          this.closures = response.data.closures || [];
          this.today = response.data.today || "";
        })
        .catch((error) => {
          this.$notifyError(error, this.$t("error_loading_closures"));
        })
        .then(() => {
          this.isLoading = false;
        });
    },
    save() {
      //the picker hands back the two ends in the order they were clicked
      const dates = [...this.range].sort();

      this.isSaving = true;
      axios
        .post("/school-closures/add", {
          starts_on: dates[0],
          ends_on: dates[dates.length - 1],
          reason: this.reason || null,
        })
        .then((response) => {
          this.$notifySuccess((response.data.success || []).join(", "));

          //what it had to undo is worth saying out loud, not just counting
          const withdrawn = response.data.withdrawn || {};
          if (withdrawn.runs) {
            this.$notifySuccess(
              this.$t("closure_withdrew", {
                runs: withdrawn.runs,
                bookings: withdrawn.bookings,
                coins: withdrawn.coins,
              })
            );
          }
          //and who was warned, because a family that is not told turns up
          if (withdrawn.families_notified) {
            this.$notifySuccess(
              this.$t("closure_families_told", {
                families: withdrawn.families_notified,
              })
            );
          }

          this.addDialog = false;
          this.load();
        })
        .catch((error) => {
          this.$notifyError(error, this.$t("error_saving_closure"));
        })
        .then(() => {
          this.isSaving = false;
        });
    },
    remove(closure) {
      this.$confirmAction({
        title: this.$t("cancel_closure"),
        text: closure.state === "past"
          ? this.$t("confirm_delete_past_closure")
          : this.$t("confirm_cancel_closure"),
        icon: "warning",
        confirmText: this.$t("yes"),
      }).then((confirmed) => {
        if (!confirmed) return;

        this.removing = closure.id;
        axios
          .post("/school-closures/remove", { closure_id: closure.id })
          .then((response) => {
            this.$notifySuccess((response.data.success || []).join(", "));
            this.load();
          })
          .catch((error) => {
            this.$notifyError(error, this.$t("error_removing_closure"));
          })
          .then(() => {
            this.removing = null;
          });
      });
    },
  },
};
</script>
